/**
 * Welcome Page - CXO Command Centre
 * Landing page with branding and login navigation
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  MessageSquare, 
  Shield, 
  ArrowRight,
  Building2,
  Lock
} from 'lucide-react';

const Welcome = () => {
  const navigate = useNavigate();

  return (
    <div style={styles.container}>
      {/* Main Content */}
      <div style={styles.content}>
        {/* Left Side - Branding */}
        <div style={styles.brandSection}>
          <div style={styles.brandBadge}>
            <Building2 size={18} />
            <span>CXO Command Centre</span>
          </div>

          <h1 style={styles.title}>CXO COMMAND CENTRE</h1>
          
          <p style={styles.subtitle}>
            Immersive workforce analytics, payroll intelligence, and AI-powered decision support
          </p>

          <div style={styles.features}>
            <div style={styles.feature}>
              <div style={styles.featureIcon}>
                <BarChart3 size={24} />
              </div>
              <div>
                <h3 style={styles.featureTitle}>Workforce Analytics</h3>
                <p style={styles.featureDesc}>Interactive Power BI insights across people and payroll data</p>
              </div>
            </div>

            <div style={styles.feature}>
              <div style={styles.featureIcon}>
                <MessageSquare size={24} />
              </div>
              <div>
                <h3 style={styles.featureTitle}>AI Assistant</h3>
                <p style={styles.featureDesc}>Natural language queries across HR and payroll intelligence</p>
              </div>
            </div>

            <div style={styles.feature}>
              <div style={styles.featureIcon}>
                <Shield size={24} />
              </div>
              <div>
                <h3 style={styles.featureTitle}>Enterprise Security</h3>
                <p style={styles.featureDesc}>MFA, RBAC & complete audit trails</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Card */}
        <div style={styles.loginSection}>
          <div style={styles.loginCard}>
            <div style={styles.loginHeader}>
              <Lock size={32} style={{ color: '#22d3ee' }} />
              <h2 style={styles.loginTitle}>Secure Access</h2>
              <p style={styles.loginSubtitle}>
                Private intelligence workspace for authorized personnel only
              </p>
            </div>

            <button 
              style={styles.loginButton}
              onClick={() => navigate('/login')}
            >
              Sign In to Dashboard
              <ArrowRight size={20} />
            </button>

            <div style={styles.securityNote}>
              <Shield size={16} />
              <span>Protected with Multi-Factor Authentication</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerLeft}>
          <Building2 size={20} />
          <span>© 2026 CXO Command Centre. All rights reserved.</span>
        </div>
        <div style={styles.footerRight}>
          Powered by <strong style={{ color: '#22d3ee' }}>Spera Digital ®</strong>
        </div>
      </footer>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'radial-gradient(circle at 15% 20%, #1e3a5f 0%, #0b1022 34%, #070b18 70%, #03050c 100%)',
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    flex: 1,
    display: 'flex',
    maxWidth: '1400px',
    width: '100%',
    margin: '0 auto',
    padding: '3rem 2rem',
    gap: '4rem',
    alignItems: 'center',
  },
  brandSection: {
    flex: 1,
    color: 'white',
  },
  brandBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    background: 'rgba(34, 211, 238, 0.08)',
    borderRadius: '9999px',
    fontSize: '0.875rem',
    marginBottom: '2rem',
    border: '1px solid rgba(34, 211, 238, 0.25)',
  },
  title: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: '3.5rem',
    fontWeight: '700',
    marginBottom: '1rem',
    background: 'linear-gradient(135deg, #ffffff 0%, #67e8f9 48%, #c4b5fd 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  subtitle: {
    fontSize: '1.25rem',
    opacity: '0.9',
    marginBottom: '3rem',
    maxWidth: '500px',
    lineHeight: '1.6',
  },
  features: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  feature: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
    padding: '1rem',
    background: 'rgba(15, 23, 42, 0.62)',
    borderRadius: '12px',
    border: '1px solid rgba(34, 211, 238, 0.14)',
  },
  featureIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, rgba(34, 211, 238, 0.22), rgba(124, 58, 237, 0.22))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#67e8f9',
    flexShrink: 0,
  },
  featureTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    marginBottom: '0.25rem',
    color: 'white',
  },
  featureDesc: {
    fontSize: '0.875rem',
    opacity: '0.8',
    margin: 0,
  },
  loginSection: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  loginCard: {
    width: '100%',
    maxWidth: '420px',
    background: 'rgba(11, 16, 34, 0.94)',
    borderRadius: '20px',
    padding: '2.5rem',
    boxShadow: '0 25px 70px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(34, 211, 238, 0.18)',
  },
  loginHeader: {
    textAlign: 'center',
    marginBottom: '2rem',
  },
  loginTitle: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#f8fafc',
    margin: '1rem 0 0.5rem',
  },
  loginSubtitle: {
    fontSize: '0.9375rem',
    color: '#94a3b8',
    lineHeight: '1.5',
  },
  loginButton: {
    width: '100%',
    padding: '1rem 1.5rem',
    fontSize: '1rem',
    fontWeight: '600',
    color: 'white',
    background: 'linear-gradient(135deg, #06b6d4 0%, #7c3aed 100%)',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s ease',
    boxShadow: '0 8px 24px rgba(34, 211, 238, 0.25)',
  },
  securityNote: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    marginTop: '1.5rem',
    padding: '0.75rem',
    background: 'rgba(15, 23, 42, 0.85)',
    borderRadius: '8px',
    fontSize: '0.8125rem',
    color: '#94a3b8',
  },
  footer: {
    padding: '1.5rem 2rem',
    background: 'rgba(0, 0, 0, 0.2)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.875rem',
  },
  footerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  footerRight: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
};

export default Welcome;
